import { format, parseISO } from "date-fns";
import * as React from "react";
import GliderComponent from "react-glider";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useLocation } from "react-router-dom";

import Breadcrumbs from "../Breadcrumbs";
import ContactForm from "../ContactForm";
import Downloads from "../Downloads";
import FAQs from "../FAQs";
import FullWidthBanner from "../FullWidthBanner";
import Gallery from "../Gallery";
import Navigation from "../Navigation";
import Page from "../Page";
import SubNavigation from "../SubNavigation";
import DEFAULT_IMAGE from "./img/default-image.png";
import styles from "./style.scss";

const LABELS = {
  MATCHCENTRE: "Matchcentre",
};

function MemberClubPage({ onGetPage, isReady, items, onGetItems }) {
  const { pathname } = useLocation();
  const getPage = React.useCallback(onGetPage, [onGetPage]);
  const [isTrackLoaded, setIsTrackLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!isReady) {
      onGetItems();
    }
  }, [onGetItems, isReady]);

  return (
    <Page path={pathname} onGetPage={getPage} scrollToTop>
      {({
        page: {
          html,
          title,
          banners,
          gallery,
          subNav,
          downloads,
          faqs,
          footerHtml,
          contactForm,
          showBreadcrumbs,
        },
        isReady: isPageReady,
      }) => {
        if (!isPageReady) {
          return null;
        }

        return (
          <section>
            {banners && Boolean(banners.length) && (
              <div className={styles.banner}>
                <GliderComponent
                  hasDots={banners.length > 1}
                  hasArrows={false}
                  slidesToShow={1}
                  duration={0.5}
                  skipTrack
                  onLoad={() => setIsTrackLoaded(true)}
                >
                  <div
                    className={`${
                      isTrackLoaded ? "" : styles.track
                    } glider-track`}
                  >
                    {banners.map(
                      (n, index) =>
                        n.images && (
                          <FullWidthBanner
                            key={n.id}
                            lazyload={index > 0}
                            media={n.images}
                            reducedHeight
                          />
                        )
                    )}
                  </div>
                </GliderComponent>
                <div className={styles.textWrapper}>
                  <div
                    className={styles.text}
                    style={{ color: banners[0].color }}
                  >
                    <h1 className={`${styles.title} ${styles.bannerTitle}`}>
                      {title}
                    </h1>
                    {showBreadcrumbs && (
                      <Breadcrumbs url={pathname} color={banners[0].color} />
                    )}
                  </div>
                </div>
              </div>
            )}
            {subNav && (
              <Navigation type={subNav}>
                {({ navigation }) => <SubNavigation navigation={navigation} />}
              </Navigation>
            )}
            <div className={styles.container}>
              <div className={styles.content}>
                {!banners && showBreadcrumbs && <Breadcrumbs url={pathname} />}
                {!banners && <h1 className={styles.title}>{title}</h1>}
                {gallery && Boolean(gallery.length) && (
                  <>
                    <Gallery data={gallery} />
                    <div className={styles.thinLine} />
                  </>
                )}
                <div
                  dangerouslySetInnerHTML={{ __html: html }}
                  className="richtext"
                />
                <div className={styles.grid}>
                  {isReady
                    ? items.map((n, index) => (
                        <div key={n.id} className={styles.gridItem}>
                          {n.imageUrl ? (
                            <img
                              src={n.imageUrl}
                              alt=""
                              loading={index > 1 ? "lazy" : "eager"}
                              width="256"
                              height="256"
                              className={styles.gridItemImage}
                            />
                          ) : (
                            <img
                              src={DEFAULT_IMAGE}
                              alt=""
                              loading={index > 1 ? "lazy" : "eager"}
                              width="256"
                              height="256"
                              className={styles.gridItemImage}
                            />
                          )}
                          <div className={styles.details}>
                            <div>
                              <span className={styles.gridItemTitle}>
                                {n.title}
                              </span>
                              {n.founded && (
                                <span className={styles.gridItemDetailsItem}>
                                  Est. {format(parseISO(n.founded), "yyyy")}
                                </span>
                              )}
                              {n.president && (
                                <span className={styles.gridItemDetailsItem}>
                                  President: {n.president}
                                </span>
                              )}
                              {n.secretary && (
                                <span className={styles.gridItemDetailsItem}>
                                  Secretary General: {n.secretary}
                                </span>
                              )}
                            </div>
                            {(Boolean(n.socialLinks?.length) ||
                              Boolean(n.websiteUrl) ||
                              Boolean(n.url)) && (
                              <div className={styles.socialLinks}>
                                <ul className={styles.social}>
                                  {n.socialLinks.map((m) => (
                                    <li
                                      key={m.id}
                                      className={styles.socialItem}
                                    >
                                      <a
                                        href={m.url}
                                        title={m.platform.toLowerCase()}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={styles.socialLink}
                                      >
                                        {n.title}
                                      </a>
                                    </li>
                                  ))}
                                  {n.websiteUrl && (
                                    <li className={styles.socialItem}>
                                      <a
                                        href={n.websiteUrl}
                                        title="website"
                                        target="_blank"
                                        rel="noreferrer"
                                        className={styles.socialLink}
                                      >
                                        website
                                      </a>
                                    </li>
                                  )}
                                  {n.url && (
                                    <li
                                      className={`${styles.socialItem} ${styles.matchCentreItem}`}
                                    >
                                      <a
                                        href={n.url}
                                        className={styles.matchCentreLink}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {LABELS.MATCHCENTRE}
                                      </a>
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    : [...Array(4)].map((n, index) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={styles.skeletonGridItem}
                        />
                      ))}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: footerHtml }}
                  className="richtext"
                />
                {downloads && Boolean(downloads.length) && (
                  <Downloads downloads={downloads} />
                )}
                {faqs && Boolean(faqs.length) && <FAQs faqs={faqs} />}
              </div>
              {contactForm && (
                <div className={styles.aside}>
                  <h2 className={styles.contactFormTitle}>
                    {contactForm.title}
                  </h2>
                  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                    <ContactForm
                      id={contactForm.id}
                      type={contactForm.type}
                      fields={contactForm.fields}
                      allowAnonymous={contactForm.allowAnonymous}
                    />
                  </GoogleReCaptchaProvider>
                </div>
              )}
            </div>
          </section>
        );
      }}
    </Page>
  );
}

export default MemberClubPage;
